import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API } from "../App";
import "../components/sojourns/sojourns.css";
import RegisterForm from "../components/sojourns/RegisterForm";
import OfficialAwardBadge from "../components/OfficialAwardBadge";
import { Enso, Film, Redaction, Reveal, Barcode, SeatPlan, Moon, useReducedMotion, useInView, useCountdown, useSojournsChrome } from "../components/sojourns/primitives";
import { Chart, Still, Invite } from "../components/sojourns/Chart";
import { FounderEdition } from "../components/sojourns/FounderEdition";
import { Follow, FollowGlyph, FollowLink, INSTAGRAM } from "../components/sojourns/Follow";
import JsonLd from "../components/JsonLd";

const FONTS = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Mulish:wght@400;600&family=Space+Mono&family=Cinzel&display=swap";
const DESC = "A month away for twelve people whose business is doing better than they are, with three business mentors. Spring 2027, by application. From Savi, 2026 Coliving Awards winner.";
// savicoliving.com/sojourns is the page's one home; savisojourns.com forwards here.
const ORIGIN = "https://www.savicoliving.com";
const PAGE_URL = "https://www.savicoliving.com/sojourns";
const OG_IMAGE = `${ORIGIN}/sojourn-media/og.jpg?v=4`;

const EVENT_LD = {
  "@context": "https://schema.org",
  "@type": "Event",
  name: "Savi Sojourns No. 001",
  alternateName: "Savi Sojourns",
  description: DESC,
  url: PAGE_URL,
  image: OG_IMAGE,
  startDate: "2027",
  duration: "P28D",
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: { "@type": "Place", name: "A private house by the Mediterranean, revealed to accepted applicants" },
  organizer: { "@type": "Organization", name: "Savi Coliving", url: ORIGIN, sameAs: ["https://savisojourns.com", INSTAGRAM, "https://www.instagram.com/savicoliving/", "https://www.linkedin.com/company/savi-coliving"] },
  audience: { "@type": "BusinessAudience", audienceType: "Business owners and founders whose business is doing better than they are" },
  maximumAttendeeCapacity: 12,
  isAccessibleForFree: false,
  keywords: "founder retreat, business retreat with mentors, sober business retreat, wellness retreat for entrepreneurs, burnout retreat for business owners, month-long retreat",
  offers: { "@type": "Offer", url: `${PAGE_URL}#register`, availability: "https://schema.org/PreOrder", priceCurrency: "EUR", validFrom: "2026-11-24", description: "Fare on application. Twelve places." },
};

const Contact = () => (
  <p className="sj-contact" data-testid="sojourns-contact">
    Steve Haynes, Savi · <a href="mailto:hola@savicoliving.com" data-testid="sojourns-contact-email">hola@savicoliving.com</a> · <FollowLink /> · <a href="https://www.savicoliving.com" target="_top" data-testid="sojourns-contact-site">savicoliving.com</a> · <Link to="/sojourns/notes" data-testid="sojourns-notes-link">Notes</Link>
  </p>
);

export default function SojournsPage() {
  const reduce = useReducedMotion();
  const [arrived, setArrived] = useState(false);
  const [skip, setSkip] = useState(false);
  const [settings, setSettings] = useState({ reveal_date: null, edition: "001", seats_held: [] });
  const [founder, setFounder] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [torn, setTorn] = useState(false);
  const [stubGone, setStubGone] = useState(false);
  const [perfRef, perfIn] = useInView("0px 0px -5% 0px");
  const days = useCountdown(settings.reveal_date);
  const [unlocked, setUnlocked] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("sj_unlock") || "null"); } catch { return null; }
  });

  useEffect(() => {
    axios.get(`${API}/sojourns/settings`).then((r) => setSettings({ seats_held: [], ...(r.data || {}) })).catch(() => {});
    axios.get(`${API}/sojourns/founder`).then((r) => setFounder(r.data || null)).catch(() => {});
  }, []);

  // Arrival: the ensō paints, then the entry fades up. Any scroll, tap or key skips to the finished state.
  useEffect(() => {
    const t = setTimeout(() => setArrived(true), 50);
    const finish = () => { setSkip(true); setArrived(true); };
    const opts = { passive: true, once: true };
    window.addEventListener("scroll", finish, opts);
    window.addEventListener("pointerdown", finish, opts);
    window.addEventListener("keydown", finish, opts);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", finish);
      window.removeEventListener("pointerdown", finish);
      window.removeEventListener("keydown", finish);
    };
  }, []);

  // Page chrome: near-black behind overscroll, no cream from the main site.
  useSojournsChrome();

  // Arriving on /sojourns#register (from a note): skip the arrival sequence and go straight to the form.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#register") return undefined;
    setSkip(true);
    setArrived(true);
    const t = setTimeout(() => document.getElementById("register")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [hash, reduce]);

  const onRegistered = () => {
    setRegistered(true);
    setTorn(true);
    setTimeout(() => setStubGone(true), reduce ? 0 : 750);
  };

  const edition = settings.edition || "001";
  // The gate opens on the day itself: from then on the page takes applications.
  const open = days !== null && days <= 0;
  const countdownText = days === null || open ? null : days > 1 ? `Gate opens in ${days} days, with the full moon` : "Gate opens tomorrow, with the full moon";

  return (
    <div className={`sj ${arrived ? "arrived" : ""} ${skip ? "skip" : ""} ${reduce ? "reduce" : ""}`} data-testid="sojourns-page">
      <Helmet>
        <title>Savi Sojourns · A month-long retreat with business mentors</title>
        <meta name="description" content={DESC} />
        <link rel="canonical" href="https://www.savicoliving.com/sojourns" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Savi Sojourns" />
        <meta property="og:title" content="Savi Sojourns" />
        <meta property="og:description" content={DESC} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="so·journ, noun. A temporary stay. Ours is a month, in spring 2027, for twelve people whose business is doing better than they are." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Savi Sojourns" />
        <meta name="twitter:description" content={DESC} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="theme-color" content="#12100E" />
        <link rel="icon" href="/sojourn-media/sojourns-favicon.ico?v=1" sizes="16x16 32x32 48x48" />
        <link rel="icon" type="image/png" sizes="32x32" href="/sojourn-media/sojourns-favicon-32.png?v=1" />
        <link rel="icon" type="image/png" sizes="192x192" href="/sojourn-media/sojourns-favicon-192.png?v=1" />
        <link rel="apple-touch-icon" sizes="180x180" href="/sojourn-media/sojourns-apple-touch-icon.png?v=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preload" as="style" href={FONTS} />
        <link rel="stylesheet" href={FONTS} />
        <link rel="preload" as="image" href="/sojourn-media/film/water.jpg" />
      </Helmet>
      <JsonLd id="sojourns-event" data={EVENT_LD} />

      {/* 1. Header */}
      <header className="sj-header" data-testid="sojourns-header">
        <div className="sj-col">
          <a
            href="#top"
            className="sj-wordmark"
            data-testid="sojourns-wordmark"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" }); }}
          >
            <span className="savi">SAVI</span>
            <span className="rule" aria-hidden="true" />
            <span className="sojourns">Sojourns</span>
          </a>
          <div className="sj-header-right">
            <FollowGlyph />
            <a href="#register" className="sj-header-link" data-testid="sojourns-header-register">{open ? "Apply now" : <><span className="sj-full">Register interest</span><span className="sj-short">Register</span></>}</a>
          </div>
        </div>
      </header>

      {/* 2. Hero: the dictionary entry */}
      <section className="sj-hero sj-film-section" data-testid="sojourns-hero">
        <Film name="water" reduce={reduce} />
        <div className="sj-col">
          <Enso reduce={reduce} />
          <h1 className="sj-entry sj-seq d1">
            <span className="word">so·journ</span>
            <span className="pos">noun</span>
          </h1>
          <div className="sj-rule sj-seq d2" aria-hidden="true" />
          <ol className="sj-senses" style={{ marginTop: 32 }}>
            <li className="sj-sense one sj-seq d2"><span className="n" aria-hidden="true">1</span>A temporary stay.</li>
            <li className="sj-sense two sj-seq d3"><span className="n" aria-hidden="true">2</span>Savi Sojourns: a month away in spring 2027 for twelve people whose business is doing better than they are.</li>
          </ol>
        </div>
        <div className="sj-hint sj-seq d4" aria-hidden="true"><span>Scroll</span><i /></div>
      </section>

      {/* 3. The month */}
      <Reveal as="section" className="sj-section tight" data-testid="sojourns-month">
        <div className="sj-col wide">
          <div className="sj-head">
            <p className="sj-label">Boarding pass</p>
            <h2 className="sj-h2">The month</h2>
          </div>
          <div className="sj-ticket" data-testid="sojourns-ticket">
            <div className="sj-ticket-head">
              <p className="sj-label">Savi Sojourns</p>
              <p className="sj-ticket-no">Sojourn <b>No. {edition}</b></p>
            </div>
            <dl className="sj-pass">
              <div className="sj-cell"><dt>Passenger</dt><dd>one of twelve, first class.</dd></div>
              <div className="sj-cell"><dt>Destination</dt><dd><Redaction reduce={reduce} text={unlocked?.destination} /></dd></div>
              <div className="sj-cell"><dt>Departs</dt><dd>spring 2027, for twenty-eight nights.</dd></div>
              <div className="sj-cell"><dt>On board</dt><dd>three business mentors, a wellness facilitator, a holistic and mental wellbeing coach and a chef.</dd></div>
              <div className="sj-cell"><dt>Returning with</dt><dd>a clearer head and a plan for the business.</dd></div>
              <div className="sj-cell"><dt>Seats</dt><dd><SeatPlan held={settings.seats_held} founder={founder?.founder_seats || []} /></dd></div>
            </dl>
          </div>
        </div>
        <div className="sj-col sj-prose">
          <p className="sj-lede">Twenty-eight nights in one house somewhere worth the flight, with everything included except getting there. Everyone arrives together and leaves together, so nobody spends the month as the new one.</p>
          <p className="sj-p">Three mentors come during the month, one a week, and stay two nights each. Between them they've scaled businesses, sold them and invested in them. Each gives an evening ‘off the record’ and a workshop, sits down with you one to one and eats at the table. Twice a week the group works on one real problem from someone's business, and what's said there stays there. You arrive with your numbers and one goal, and you go home with a 90-day plan.</p>
          <p className="sj-p">Mornings are for breathwork, yoga, mobility or a swim in the sea, and Savi's holistic and mental wellbeing coach stays the whole month. Dinner is at one long table, cooked by a chef. The month is alcohol-free, in the house and out of it.</p>
          <p className="sj-p" data-testid="sojourns-award-line">Sojourns is run by Savi, winner of Destination Coliving at the 2026 Coliving Awards.</p>
        </div>
      </Reveal>

      {/* 3a. Founder Edition: a few seats sold before anything is known */}
      {founder?.enabled && (
        <section className="sj-section tight" id="founder" data-testid="sojourns-founder-section">
          <div className="sj-col wide">
            <FounderEdition
              state={founder}
              setState={setFounder}
              onRegister={() => document.getElementById("register")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" })}
            />
          </div>
        </section>
      )}

      {/* 3b. The chart: a real coastline, everything that names it under black */}
      <Reveal as="section" className="sj-section tight" data-testid="sojourns-destination-chart">
        <div className="sj-col wide">
          <div className="sj-head">
            <p className="sj-label">Chart</p>
            <h2 className="sj-h2">Somewhere worth the flight.</h2>
          </div>
          <Chart reduce={reduce} unlocked={unlocked} />
          {settings.invite_enabled && !unlocked && (
            <div className="sj-invite-wrap"><Invite onUnlock={setUnlocked} /></div>
          )}
        </div>
      </Reveal>

      {/* 3c. One long table at dusk */}
      <Reveal as="section" className="sj-section tight" data-testid="sojourns-table">
        <Still />
      </Reveal>

      {/* 4. Eligibility, with the film */}
      <Reveal as="section" className="sj-section sj-film-section" data-testid="sojourns-eligibility">
        <Film name="candles" reduce={reduce} />
        <div className="sj-col narrow sj-centre">
          <div className="sj-head">
            <p className="sj-label">Eligibility</p>
            <h2 className="sj-h2">Your business is doing better than you are.</h2>
            <div className="sj-rule" aria-hidden="true" />
          </div>
          <p className="sj-p">You own something that works. It pays well and it's growing, and it takes more out of you than it should. You want the next stage to go better, for the business and for you.</p>
          <p className="sj-p">A distinctive experience comes with a price to match. The fare is on application, and there are twelve places.</p>
        </div>
      </Reveal>

      {/* 5. How it works */}
      <Reveal as="section" className="sj-section" data-testid="sojourns-how">
        <div className="sj-col wide">
          <div className="sj-head">
            <p className="sj-label">How it works</p>
            <h2 className="sj-h2">By application</h2>
          </div>
          <ol className="sj-steps">
            <li><span className="n" aria-hidden="true">01</span><p>Register your interest below and follow <a href={INSTAGRAM} className="sj-link" target="_blank" rel="noopener noreferrer" data-testid="sojourns-how-instagram">@savisojourns</a>, where most of what we say about Sojourn {edition} will appear first.</p></li>
            <li><span className="n" aria-hidden="true">02</span><p>When applications open, you apply and we have a 30-minute call, so we both know it's right and agree the goal you'll bring.</p></li>
            <li><span className="n" aria-hidden="true">03</span><p>If it's a fit, you get an offer, held for seven days, and a deposit holds your room.</p></li>
          </ol>
        </div>
      </Reveal>

      {/* 6. Register your interest */}
      <Reveal as="section" className="sj-section" id="register" data-testid="sojourns-register">
        <div className="sj-col narrow">
          <div className="sj-award">
            <OfficialAwardBadge testid="sojourns-award-badge" width={240} plate={false} className="sj-award-plate" alt="Coliving Awards 2026 Winner, Destination Coliving" />
          </div>
          <div className="sj-head">
            <p className="sj-label">Sojourn No. {edition}</p>
            <h2 className="sj-h2">{open ? "Apply for your place" : "Register your interest"}</h2>
          </div>
          {open && !registered && (
            <p className="sj-count" role="status" data-testid="sojourns-countdown">Gate open · twelve places</p>
          )}
          {countdownText && !open && !registered && (
            <p className="sj-count" role="status" aria-live="polite" data-testid="sojourns-countdown">{countdownText}</p>
          )}
          {!registered && <RegisterForm onSuccess={onRegistered} apply={open} />}
          {registered && (
            <div className="sj-success" role="status" aria-live="polite" data-testid="sojourns-success">
              {open ? (
                <>
                  <h3>Your application is in.</h3>
                  <p>Steve will be in touch within a few days to arrange a 30-minute call.</p>
                </>
              ) : (
                <>
                  <h3>You're on the list.</h3>
                  <p>You'll hear about Sojourn {edition} before anyone else.</p>
                </>
              )}
              <Follow quiet line="Most of the news lands on Instagram first. Keep an eye on it." testid="sojourns-success-follow" />
            </div>
          )}
        </div>
      </Reveal>

      {/* 6a. Instagram */}
      <Reveal as="section" className="sj-section tight" data-testid="sojourns-instagram">
        <div className="sj-col narrow sj-centre">
          <Follow />
        </div>
      </Reveal>

      {/* 7. The stub: tears away along the perforation once someone registers */}
      {stubGone ? (
        <footer className="sj-stub" data-testid="sojourns-stub-gone">
          <div className="sj-col wide"><Contact /></div>
        </footer>
      ) : (
        <footer className={`sj-stub ${torn ? "torn" : ""}`} data-testid="sojourns-stub" aria-hidden={torn}>
          <div className="sj-col wide">
            <div ref={perfRef} className={`sj-perf ${perfIn ? "in" : ""}`} aria-hidden="true" />
            <div className="sj-stub-grid">
              <div>
                <p className="sj-label">Sojourn</p>
                <p className="sj-edition" data-testid="sojourns-edition">No. {edition}</p>
              </div>
              <div className="sj-barcode">
                <Barcode />
              </div>
              <div className={`sj-gate ${open ? "open" : ""}`}>
                <Moon days={open ? 0 : days} />
                <div>
                  {open ? (
                    <>
                      <p className="sj-label">Gate open</p>
                      <a href="#register" className="sj-gate-apply" data-testid="sojourns-stub-apply">Apply now</a>
                    </>
                  ) : (
                    <>
                      <p className="sj-label">Gate opens with the full moon</p>
                      {countdownText && <p data-testid="sojourns-stub-countdown">{countdownText.replace("Gate opens ", "").replace(", with the full moon", "")}</p>}
                    </>
                  )}
                </div>
              </div>
            </div>
            <Contact />
          </div>
        </footer>
      )}
    </div>
  );
}
